import {
  SitePageResponse,
  type WebRouteDefinition,
  type WebRouteTransportContext,
} from "@brains/plugins";
import { z } from "@brains/utils/zod";
import type { ContactAdmission, ContactDenialReason } from "./admission";
import type { ContactIntake } from "./intake";
import { contactSubmissionSchema } from "./entity/schema";
import { ContactHttpError, readContactForm } from "./http-body";
import { isPrivatePeer } from "./network";
import {
  CONTACT_SLOT,
  contactForm,
  contactPage,
  contactRefused,
  contactThanks,
  contactUnavailable,
  type ContactDraft,
  type ContactPresentation,
} from "./http-page";

export interface ContactHttpPolicy {
  origin: string;
  maxBodyBytes: number;
  readTimeoutMs: number;
  /** Behind a TLS-terminating proxy on a private network (Kamal's), believe
   * its X-Forwarded-Proto: https. Only the protocol; never a visitor address. */
  trustForwardedProto?: boolean | undefined;
}
/** Loopback names: the addresses, localhost and its subdomains (RFC 6761). */
function isLoopback(hostname: string): boolean {
  return (
    ["localhost", "127.0.0.1", "[::1]"].includes(hostname) ||
    hostname.endsWith(".localhost")
  );
}
const originSchema: z.ZodString = z
  .string()
  .url()
  .refine((value) => {
    const url = new URL(value);
    return (
      value === url.origin &&
      (url.protocol === "https:" ||
        (url.protocol === "http:" && isLoopback(url.hostname)))
    );
  }, "An exact HTTPS origin or loopback HTTP origin is required");

/**
 * The preview host a deployment serves beside its origin: its domain's
 * preview host, or locally the `preview.` host the webserver treats as
 * preview (`http://localhost:8080` beside `http://preview.localhost:8080`).
 */
export function previewOriginFor(
  origin: string,
  deploymentPreview: string | undefined,
): string | undefined {
  if (deploymentPreview) return deploymentPreview;
  const url = new URL(origin);
  if (url.protocol !== "http:" || url.hostname !== "localhost")
    return undefined;
  url.hostname = "preview.localhost";
  return url.origin;
}
export const contactHttpPolicyShape: z.ZodObject<
  {
    origin: z.ZodString;
    maxBodyBytes: z.ZodNumber;
    readTimeoutMs: z.ZodNumber;
    trustForwardedProto: z.ZodOptional<z.ZodBoolean>;
  },
  z.core.$strict
> = z.strictObject({
  origin: originSchema,
  maxBodyBytes: z.number().int().min(256).max(65536),
  readTimeoutMs: z.number().int().min(100).max(30000),
  trustForwardedProto: z.boolean().optional(),
});
export const contactHttpPolicySchema: z.ZodType<ContactHttpPolicy> =
  contactHttpPolicyShape;
export interface ContactHttpOptions {
  themeCSS?: string | undefined;
  /** The deployment's preview origin, served alongside the policy origin. */
  previewOrigin?: string | undefined;
  /** Who notes go to, as the site names its owner; read per request. */
  owner?: (() => string | undefined) | undefined;
  /** The site's own theme, used when the visitor's link names none; read per request. */
  defaultTheme?: (() => "light" | "dark" | undefined) | undefined;
}
const formSchema = z.strictObject({
  token: z.string().regex(/^[a-f0-9]{64}$/),
  ...contactSubmissionSchema.shape,
});
/** A site links a topic to the form; it starts the visitor's message, who can change it. */
const TOPIC_MAX_LENGTH = 200;
function topicDraft(url: URL): ContactDraft {
  const topic = url.searchParams
    .get("topic")
    ?.trim()
    .slice(0, TOPIC_MAX_LENGTH);
  return topic ? { message: `${topic}\n\n` } : {};
}
const headers = {
  "Content-Type": "text/html; charset=utf-8",
  "Cache-Control": "no-store",
  // no-referrer makes native browser POSTs send Origin: null. same-origin
  // preserves the origin check while still suppressing cross-site referrers.
  "Referrer-Policy": "same-origin",
  "X-Content-Type-Options": "nosniff",
  // The page is the site's own contact page with the form in it, so the
  // site's styles, fonts and scripts load; the form still posts only here and
  // the page cannot be framed.
  "Content-Security-Policy":
    "default-src 'self'; script-src 'self' 'unsafe-inline'; style-src 'self' 'unsafe-inline' https:; font-src 'self' https: data:; img-src 'self' https: data:; form-action 'self'; base-uri 'none'; frame-ancestors 'none'",
};

/** A contact page: the section fills the site's own page, or stands in the fallback page. */
function page(
  section: string,
  presentation: ContactPresentation,
  status = 200,
): SitePageResponse {
  return new SitePageResponse(contactPage(section, presentation), {
    status,
    headers,
    slot: { name: CONTACT_SLOT, html: section },
  });
}
function denial(reason: ContactDenialReason): ContactHttpError {
  switch (reason) {
    case "invalid-network":
      return new ContactHttpError(
        403,
        "Notes can’t be sent from this connection.",
      );
    case "invalid-submission":
      return new ContactHttpError(
        400,
        "Check the name, email and message fields before retrying.",
      );
    case "invalid-token":
      return new ContactHttpError(
        409,
        "This form is unavailable or expired. Copy your message before opening a new form.",
      );
    case "submission-conflict":
      return new ContactHttpError(
        409,
        "This form already sent a different note. To send another, open a new form.",
      );
    case "rate-limited":
      return new ContactHttpError(
        429,
        "Too many requests. Please wait before retrying this form.",
      );
    case "capacity":
      return new ContactHttpError(
        503,
        "There’s no room for new notes right now. Please try this same form again later.",
      );
    case "unavailable":
      return new ContactHttpError(
        503,
        "Your note couldn’t be confirmed as saved. Send this same form again rather than opening a new one.",
      );
  }
}

/** Bounded HTTP boundary; the owning plugin controls readiness and opt-in mounting. */
export class ContactHttpHandlers {
  private readonly policy: ContactHttpPolicy;
  private readonly admission: ContactAdmission;
  private readonly intake: ContactIntake;
  private readonly themeCSS: string;
  private readonly owner: () => string | undefined;
  private readonly defaultTheme: () => "light" | "dark" | undefined;
  private readonly origins: readonly string[];
  constructor(
    admission: ContactAdmission,
    intake: ContactIntake,
    policy: ContactHttpPolicy,
    options: ContactHttpOptions = {},
  ) {
    this.themeCSS = options.themeCSS ?? "";
    this.owner = options.owner ?? ((): undefined => undefined);
    this.defaultTheme = options.defaultTheme ?? ((): undefined => undefined);
    this.admission = admission;
    this.intake = intake;
    this.policy = contactHttpPolicySchema.parse(policy);
    this.origins = [
      this.policy.origin,
      ...(options.previewOrigin
        ? [originSchema.parse(options.previewOrigin)]
        : []),
    ];
  }

  /** The URL the visitor used. A trusted private proxy's forwarded https
   * replaces the plain http it forwards on; nothing else is taken from headers. */
  private visitorUrl(
    request: Request,
    transport?: WebRouteTransportContext,
  ): URL {
    const url = new URL(request.url);
    if (
      this.policy.trustForwardedProto === true &&
      url.protocol === "http:" &&
      isPrivatePeer(transport?.remoteAddress) &&
      request.headers.get("x-forwarded-proto")?.trim().toLowerCase() === "https"
    )
      url.protocol = "https:";
    return url;
  }

  routes(preview = false): WebRouteDefinition[] {
    return [
      { path: "/contact", method: "GET" as const },
      { path: "/contact", method: "POST" as const },
      { path: "/contact/thanks", method: "GET" as const },
    ].map((route) => ({
      ...route,
      public: true,
      preview,
      handler: (request, transport) => this.handle(request, transport),
    }));
  }

  private presentation(request: Request): ContactPresentation {
    const chosen = new URL(request.url).searchParams.get("theme");
    const theme =
      chosen === "light" || chosen === "dark" ? chosen : this.defaultTheme();
    const owner = this.owner()?.trim();
    return {
      themeCSS: this.themeCSS,
      ...(theme ? { theme } : {}),
      ...(owner ? { owner } : {}),
    };
  }

  unavailable(request: Request): Response {
    return page(contactUnavailable(), this.presentation(request), 503);
  }

  async handle(
    request: Request,
    transport?: WebRouteTransportContext,
  ): Promise<Response> {
    const presentation = this.presentation(request);
    let token = "";
    let draft: ContactDraft = {};
    try {
      const url = this.visitorUrl(request, transport);
      if (!this.origins.includes(url.origin))
        throw new ContactHttpError(403, "Contact request denied.");
      if (
        url.protocol === "http:" &&
        !["127.0.0.1", "::1", "::ffff:127.0.0.1"].includes(
          transport?.remoteAddress ?? "",
        )
      )
        throw new ContactHttpError(403, "Contact request denied.");
      if (!["/contact", "/contact/thanks"].includes(url.pathname))
        throw new ContactHttpError(404, "Page not found.");
      if (
        !["GET", "POST"].includes(request.method) ||
        (url.pathname === "/contact/thanks" && request.method !== "GET")
      )
        throw new ContactHttpError(405, "Method not allowed.");
      if (
        request.method === "POST" &&
        (request.headers.get("origin") !== url.origin ||
          request.headers.get("sec-fetch-site") === "cross-site")
      )
        throw new ContactHttpError(403, "Contact request denied.");
      const gate = await this.admission.checkRequest(transport?.remoteAddress);
      if (gate.kind === "denied") throw denial(gate.reason);
      request.signal.throwIfAborted();
      if (url.pathname === "/contact/thanks")
        return page(contactThanks(presentation), presentation);
      if (request.method === "GET") {
        const form = await this.admission.issue(transport?.remoteAddress);
        if (form.kind === "denied") throw denial(form.reason);
        return page(
          contactForm(form.token, topicDraft(url), undefined, presentation),
          presentation,
        );
      }
      if (
        request.headers
          .get("content-type")
          ?.split(";")[0]
          ?.trim()
          .toLowerCase() !== "application/x-www-form-urlencoded"
      )
        throw new ContactHttpError(
          415,
          "A URL-encoded form is required. Attachments are not accepted.",
        );
      const fields = await readContactForm(
        request,
        this.policy.maxBodyBytes,
        this.policy.readTimeoutMs,
      );
      draft = {
        name: fields["name"] ?? "",
        email: fields["email"] ?? "",
        message: fields["message"] ?? "",
      };
      token = /^[a-f0-9]{64}$/.test(fields["token"] ?? "")
        ? (fields["token"] ?? "")
        : "";
      const parsed = formSchema.safeParse(fields);
      if (!parsed.success) throw denial("invalid-submission");
      const { token: credential, ...submission } = parsed.data;
      const result = await this.intake.submit(
        credential,
        submission,
        transport?.remoteAddress,
        request.signal,
      );
      if (result.kind === "denied") throw denial(result.reason);
      return new Response(null, {
        status: 303,
        headers: {
          ...headers,
          Location: `/contact/thanks${presentation.theme ? `?theme=${presentation.theme}` : ""}`,
        },
      });
    } catch (error) {
      const failure =
        error instanceof ContactHttpError ? error : denial("unavailable");
      const section = token
        ? contactForm(token, draft, failure.message, presentation)
        : contactRefused(failure.message, presentation);
      return page(section, presentation, failure.status);
    }
  }
}
